import type { VoidFunctionComponent } from 'react';

import { ReportOptionExclusionDetails } from '../../../../utils';
import { reportTypeFormatMap } from '../../../constants';
import type { SelectOption, SetState } from '../../../../../../../../types';
import type { ReportCategory } from '../../../../../shared/types';
import type { FormatSelection } from '../../FormatSelect';

import { PackageLabelsExclusion } from './PackageLabelsExclusion';

type Props = {
  format: FormatSelection;
  category: ReportCategory;
  values: SelectOption[];
  setValues: SetState<SelectOption[]>;
};

/**
 * This component is used to render a group of exclusions.
 * It will only render the exclusion if the format and category are applicable.
 * This follows a slightly different pattern than the other filters because the exclusions are more than just a list
 * of boolean options and need to be passed a list of string options.
 *
 * As more exclusions are added in the ReportOptionExclusionDetails object in
 * app/components/projects/generic/ReportGenerationV2/utils.ts
 * this component will need to be updated to support them.
 */
export const ExclusionGroup: VoidFunctionComponent<Props> = ({
  format,
  category,
  values,
  setValues,
}) => {
  const shouldShowPackageLabels = Object.values(ReportOptionExclusionDetails).filter(
    ({ format: applicableFormats, category: applicableCategories, key }) => {
      const isCorrectCategory = applicableCategories.includes(category);
      const isApplicableFormat = applicableFormats.has(reportTypeFormatMap[format.value]);
      // Package labels exclusions are supported for both projects and release groups
      const isSupportedScope = key === 'packageLabels';
      return isCorrectCategory && isApplicableFormat && isSupportedScope;
    },
  ).some(({ key }) => key === ReportOptionExclusionDetails.packageLabels.key);

  return shouldShowPackageLabels ? (
    <PackageLabelsExclusion packageLabels={values} setPackageLabels={setValues} />
  ) : null;
};
